import * as React from "react"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import TextField from "@mui/material/TextField"
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddCircleSharpIcon from '@mui/icons-material/AddCircleSharp';
import { AXIOS_API_INSTANCE, CUSTOMER_INTEGRATION_ATTRIBUTES_FRIENDLY_NAMES, STATE_ABBREVIATIONS } from "../Constants"
import { send_authorized_request } from "../Constants/cognito"
import { useSnackbar } from 'notistack';
import { AppBar, Autocomplete, Box, CircularProgress, FormControl, IconButton, Stack, Tab, Tabs, Toolbar, Tooltip, Typography, useMediaQuery } from "@mui/material"
import Grid from "@mui/material/Grid2"
import { MuiTelInput } from 'mui-tel-input'
import TabPanel from "../Utils/tabpanel"
import { DataGrid } from "@mui/x-data-grid"
import { serviceOrderChip } from "../Utils/service"
import dayjs from "dayjs"
import { useTrackedUser } from "./UserState"
import { useNavigate } from "react-router-dom"
import { LoadingBackdropIndeterminate } from "./LoadingBackdrop"
import { MaterialUISwitchTextEmail } from "../Utils/MaterialUISwitch"
import CustomerContactModal from "./CustomerContactModal"
import { useTheme } from "@emotion/react"
import ConfirmModal from "./modals/ConfirmModal"
import { formatPhoneNumber } from "../Utils"
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import CustomerItemModal from "./Service/Modals/CustomerItemModal"
import CustomerItemHistoryModal from "./Service/Modals/CustomerItemHistoryModal"
import { useSmsUnread } from "./ReactQuery/QuerySmsUnread"
import { ChatAppContacts, ChatAppMessages, ChatAppStyledPaper, ChatAppTypeMessage } from "./CRM/sms"

const CustomerDetails = ({ customer, closeModal, creatingNewCustomer }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [my_customer, setCustomer] = React.useState(customer)
    const [saving, setSaving] = React.useState(false)
    const [deleting, setDeleting] = React.useState(false)
    React.useEffect(() => {
        setCustomer(customer)
    }, [customer])

    const handleChange = (prop) => (event, extra) => {
        if (['phone'].includes(prop)) {
            if (extra.nationalNumber !== null && extra.nationalNumber !== undefined && extra.nationalNumber.length < 11) {
                setCustomer({ ...my_customer, [prop]: extra.nationalNumber })
            }
        } else if (['zip'].includes(prop)) {
            if (/^\d{0,5}(-\d{0,4})?$/.test(event.target.value)) {
                setCustomer({ ...my_customer, [prop]: event.target.value })
            }
        } else {
            setCustomer({ ...my_customer, [prop]: event.target.value })
        }
    }

    const save = (event) => {
        delete my_customer.date_created
        delete my_customer.date_updated
        if (creatingNewCustomer) {
            setSaving(true)
            send_authorized_request(() =>
                AXIOS_API_INSTANCE.put(`customers`, my_customer).then(() => {
                    enqueueSnackbar('Successfully created customer', { variant: 'success' })
                    setSaving(false)
                    closeModal(true)
                }).catch((error) => {
                    enqueueSnackbar(`Failed to create customer <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
                    setSaving(false)
                })
            )
        } else {
            setSaving(true)
            send_authorized_request(() =>
                AXIOS_API_INSTANCE.post(
                    `customers/${my_customer.id}`,
                    my_customer
                ).then(() => {
                    enqueueSnackbar('Successfully updated customer', { variant: 'success' })
                    setSaving(false)
                    closeModal(true)
                }).catch((error) => {
                    enqueueSnackbar(`Failed to update customer <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
                    setSaving(false)
                })
            )
        }
    }
    const delete_customer = () => {
        setDeleting(true)
        send_authorized_request(() =>
            AXIOS_API_INSTANCE.delete(`customers/${my_customer.id}`).then(() => {
                enqueueSnackbar('Successfully deleted customer', { variant: 'success' })
                setDeleting(false)
                closeModal(true)
            }).catch((error) => {
                enqueueSnackbar(`Failed to delete customer <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
                setDeleting(false)
            })
        )
    }

    return (
        <Box>
            <LoadingBackdropIndeterminate
                open={saving}
                text={"Saving User"}
            />
            <LoadingBackdropIndeterminate
                open={deleting}
                text={"Deleting User"}
            />
            <Grid container spacing={0} columnSpacing={1} columns={12}>
                <Grid container direction="column" size={8}>
                    <Grid container spacing={1} size="grow">
                        <Grid size={6}>
                            <TextField
                                margin="dense"
                                id="name"
                                label="Name"
                                type="text"
                                fullWidth
                                variant="filled"
                                value={my_customer?.name}
                                onChange={handleChange("name")}
                            />
                        </Grid>
                    </Grid>
                    <Grid container size={'grow'}>
                        <Grid size={8} >
                            <TextField
                                margin="dense"
                                id="address_1"
                                label="Address 1"
                                type="text"
                                fullWidth
                                variant="filled"
                                value={my_customer?.address_1}
                                onChange={handleChange("address_1")}
                            />
                        </Grid>
                    </Grid>
                    <Grid container size={'grow'}>
                        <Grid size={8} >
                            <TextField
                                margin="dense"
                                id="address_2"
                                label="Address 2"
                                type="text"
                                fullWidth
                                variant="filled"
                                value={my_customer?.address_2}
                                onChange={handleChange("address_2")}
                            />
                        </Grid>
                    </Grid>
                    <Grid container spacing={1} size="grow">
                        <Grid size={5}>
                            <TextField
                                margin="dense"
                                id="city"
                                label="City"
                                type="text"
                                fullWidth
                                variant="filled"
                                value={my_customer?.city}
                                onChange={handleChange("city")}
                            />
                        </Grid>
                        <Grid size={"auto"}>
                            <FormControl variant="filled" sx={{ mt: 1, minWidth: 80 }}>
                                <Autocomplete
                                    options={STATE_ABBREVIATIONS}
                                    isOptionEqualToValue={(option, value) => option === value}
                                    autoComplete
                                    includeInputInList
                                    disableClearable
                                    value={my_customer?.state ?? ''}
                                    onChange={(e, val) => setCustomer({ ...my_customer, state: val })}
                                    renderInput={(params) => {
                                        return (<TextField {...params} label="State" variant="filled" fullWidth autoComplete='off' />)
                                    }}
                                    renderOption={(props, option, { inputValue }) => {
                                        const matches = match(props.key, inputValue, { insideWords: true, findAllOccurrences: true });
                                        const parts = parse(props.key, matches);
                                        props.key = props.id

                                        return (
                                            <li {...props} >
                                                <div>
                                                    {parts.map((part, index) => (
                                                        <span
                                                            key={index}
                                                            style={{
                                                                fontWeight: part.highlight ? 700 : 400,
                                                            }}
                                                        >
                                                            {part.text}
                                                        </span>
                                                    ))}
                                                </div>
                                            </li>
                                        );
                                    }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid size={4}>
                            <TextField
                                margin="dense"
                                id="zip"
                                label="ZIP / Postal"
                                type="number"
                                inputMode="numeric"
                                fullWidth
                                variant="filled"
                                value={my_customer?.zip}
                                onChange={handleChange("zip")}
                            />
                        </Grid>
                    </Grid>
                    <Grid container spacing={1} alignItems={'center'}>
                        <Grid size="auto">
                            <span>Communication Preference:</span>
                        </Grid>
                        <Grid size="grow">
                            <Stack direction="row" component="label" alignItems="center" justifyContent="flex-begin">
                                <Typography sx={{ fontWeight: 'bold' }}>Text</Typography>
                                <MaterialUISwitchTextEmail onChange={(e) => setCustomer({ ...my_customer, preferred_communication_type: e.target.checked ? 'email' : 'text' })} checked={my_customer?.preferred_communication_type === 'email'} />
                                <Typography sx={{ fontWeight: 'bold' }}>Email</Typography>
                            </Stack>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid size={4}>
                    <Grid container direction="column">
                        <Grid size="grow">
                            <TextField
                                margin="dense"
                                id="email"
                                label="Email"
                                type="email"
                                fullWidth
                                variant="filled"
                                value={my_customer?.email}
                                onChange={handleChange("email")}
                            />
                        </Grid>
                        <Grid size="grow">
                            <TextField
                                margin="dense"
                                id="company_name"
                                label="Company Name"
                                type="text"
                                fullWidth
                                variant="filled"
                                value={my_customer?.company_name}
                                onChange={handleChange("company_name")}
                            />
                        </Grid>
                        <Grid size="grow">
                            <MuiTelInput
                                margin="dense"
                                id="phone"
                                label="Phone"
                                type="tel"
                                inputMode="numeric"
                                fullWidth
                                variant="filled"
                                value={`+1 ${my_customer?.phone}`}
                                onChange={handleChange("phone")}
                                onlyCountries={['US']}
                                defaultCountry="US"
                                forceCallingCode
                                disableDropdown
                            />
                        </Grid>
                        {Object.keys(my_customer?.integration_attrs ?? {}).map((attr) =>
                            <Grid size="grow" key={attr}>
                                <span><b>{CUSTOMER_INTEGRATION_ATTRIBUTES_FRIENDLY_NAMES[attr]}:</b> {my_customer.integration_attrs[attr]}</span>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Grid>
            <Grid container justifyContent="space-between" sx={{ padding: "10px 30px 10px 30px", width: '100%' }}>
                <Grid size="auto">
                    {!creatingNewCustomer &&
                        <Tooltip title={my_customer?.deletable_reason}>
                            <span> {/* span required for tooltip events */}
                                <Button disabled={!my_customer?.deletable} variant='outlined' color='error' onClick={delete_customer} sx={{ mr: 1 }}>
                                    <DeleteIcon /> <Box sx={{ pl: 1 }}><span>Delete</span></Box>
                                </Button>
                            </span>
                        </Tooltip>
                    }
                </Grid>
                <Grid size="auto">
                    <Button variant="contained" autoFocus onClick={save}>
                        {creatingNewCustomer ? "Create" : "Save"}
                    </Button>
                </Grid>
            </Grid>
        </Box>
    )
}

const CustomerContacts = ({ customer, open, closeModal }) => {
    const theme = useTheme();
    const { enqueueSnackbar } = useSnackbar();
    const [rowData, setRowData] = React.useState({ rows: undefined, total_count: 0 })
    const [deleting, setDeleting] = React.useState(false)
    const [isLoading, setIsLoading] = React.useState(true)
    const [selectedContact, setSelectedContact] = React.useState(undefined)
    const [confirmModal, setConfirmModal] = React.useState({
        open: false,
        text: '',
        callback: null
    })
    const isLgUp = useMediaQuery(theme.breakpoints.up('lg'))
    React.useEffect(() => {
        if (open && rowData.rows === undefined) {
            getCustomerContacts()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    const createCustomerContactClick = () => {
        setSelectedContact({
            customer_id: customer.id,
            first_name: '',
            last_name: '',
            phone: '',
            email: '',
            preferred_communication_type: 'text',
            creatingNew: true
        })
    }

    const deleteContact = (contact) => {
        setConfirmModal({
            open: true,
            text: `Are you sure you want to delete contact ${contact.first_name} ${contact.last_name}?`,
            callback: (confirmation) => {
                if (confirmation) {
                    setDeleting(true)
                    send_authorized_request(() =>
                        AXIOS_API_INSTANCE.delete(`customercontacts/${contact.id}`).then((response) => {
                            enqueueSnackbar(`Successfully deleted contact`, { variant: 'success' })
                            setDeleting(false)
                            getCustomerContacts()
                        }).catch((error) => {
                            setDeleting(false)
                            enqueueSnackbar(`Deleting contact failed <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
                        })
                    )
                }
                setConfirmModal({ ...confirmModal, open: false, text: `Are you sure you want to delete contact ${contact.first_name} ${contact.last_name}?` })
            }
        })
    }

    const getCustomerContacts = () => {
        setIsLoading(true)
        send_authorized_request(() =>
            AXIOS_API_INSTANCE.get("customercontacts", {
                params: {
                    order_by: 'first_name',
                    order_by_direction: 'asc',
                    filters: JSON.stringify({ 'customer_id.eq': customer.id })
                },
            }).then((customercontacts) => {
                setRowData({
                    rows: customercontacts.data.data,
                    total_count: customercontacts.data.total_count,
                })
                setIsLoading(false)
            }
            ).catch((error) => {
                enqueueSnackbar(`Failed to load customer contacts: ${error.response?.data?.message ?? "Unknown Error Occurred"}`, { variant: 'error' })
                setIsLoading(false)
            })
        )
    }

    const columns = [
        {
            field: "first_name",
            headerName: "First Name",
            flex: 1,
            editable: false,
            sortable: true,
        },
        {
            field: "last_name",
            headerName: "Last Name",
            flex: 1,
            editable: false,
            sortable: true,
        },
        {
            field: "phone",
            headerName: "Phone",
            flex: 1,
            editable: false,
            sortable: true,
            valueFormatter: (value) => formatPhoneNumber(value)
        },
        {
            field: "email",
            headerName: "Email",
            flex: 1,
            editable: false,
            sortable: true,
        },
        {
            field: 'action',
            headerName: '',
            type: "actions",
            width: isLgUp ? 200 : 100,
            sortable: false,
            disableClickEventBubbling: true,

            renderCell: (params) => {
                return (
                    <Stack direction="row" spacing={2} alignItems={'center'}>
                        {isLgUp ?
                            <Button variant="contained" color="warning" size="small" onClick={() => { setSelectedContact(params.row) }} startIcon={<EditIcon />}>Edit</Button>
                            :
                            <Button variant="contained" color="warning" size="small" onClick={() => { setSelectedContact(params.row) }} sx={{ minWidth: 0 }}><EditIcon /></Button>
                        }
                        {isLgUp ?
                            <Button variant="contained" color="error" size="small" onClick={() => { deleteContact(params.row) }} startIcon={<DeleteIcon />}>Delete</Button>
                            :
                            <Button variant="contained" color="error" size="small" onClick={() => { deleteContact(params.row) }} sx={{ minWidth: 0 }}><DeleteIcon /></Button>
                        }
                    </Stack>
                );
            },
        }
    ]

    return (
        <Box>
            <LoadingBackdropIndeterminate
                open={deleting}
                text={"Deleting Contact"}
            />
            <Grid container justifyContent={'flex-end'} sx={{ mb: 1 }}>
                <Grid size="auto">
                    <Button onClick={createCustomerContactClick} variant="contained" color="primary"><AddCircleSharpIcon /><Box sx={{ pl: 1 }}>Add Contact</Box></Button>
                </Grid>
            </Grid>
            <DataGrid
                key={open}
                rows={rowData.rows}
                columns={columns}
                autoHeight={true}
                loading={isLoading}
                pageSizeOptions={[5, 10, 25]}
                pagination
                disableRowSelectionOnClick
            />
            <CustomerContactModal
                customer={customer}
                customerContact={selectedContact}
                creatingNew={selectedContact?.creatingNew ?? false}
                open={selectedContact !== undefined}
                closeModal={(updates) => {
                    if (updates) {
                        getCustomerContacts()
                    }
                    setSelectedContact(undefined)
                }}
            />
            <ConfirmModal
                {...confirmModal}
            />
        </Box>
    )

}

const CustomerCustomerItems = ({ customer, open, closeModal }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [rowData, setRowData] = React.useState({ rows: undefined, total_count: 0 })
    const [isLoading, setIsLoading] = React.useState(true)
    const [customerItemModal, setCustomerItemModal] = React.useState({
        open: false,
        customerItem: {},
        creatingNewCustomerItem: false,
    })
    const [customerItemHistoryModal, setCustomerItemHistoryModal] = React.useState({
        open: false,
        customerItem: {},
        creatingNewCustomerItem: false,
    })

    const historyCustomerItemRowClick = (row) => {
        setCustomerItemHistoryModal({
            customerItem: row,
            open: true,
            creatingNewCustomerItem: false,
        })
    }

    const editCustomerItemRowClick = (row) => {
        setCustomerItemModal({
            customerItem: row,
            open: true,
            creatingNewCustomerItem: false,
        })
    }
    const closeCustomerItemModal = (changes) => {
        setCustomerItemModal({ ...customerItemModal, open: false })
        if (changes) {
            getCustomerItems()
        }
    }

    const getCustomerItems = () => {
        setIsLoading(true)
        send_authorized_request(() =>
            AXIOS_API_INSTANCE.get("customeritems", {
                params: {
                    order_by: 'date_created',
                    order_by_direction: 'desc',
                    filters: JSON.stringify({ 'customer_id.eq': customer.id })
                },
            }).then((customeritems) => {
                console.log(customeritems)
                setRowData({
                    rows: customeritems.data.data,
                    total_count: customeritems.data.total_count,
                })
                setIsLoading(false)
            }
            ).catch((error) => {
                enqueueSnackbar(`Failed to load customer items: ${error.response?.data?.message ?? "Unknown Error Occurred"}`, { variant: 'error' })
                setIsLoading(false)
            })
        )
    }

    const columns = [
        {
            field: "identifier",
            headerName: "Identifier",
            flex: 1,
            minWidth: 100,
            editable: false,
            sortable: true,
            renderCell: (params) => {
                return (<span>{params.row.id === -1 && <b>(NEW) </b>}{params.value}</span>)
            }
        },
        {
            field: "manufacturer",
            headerName: "Manufacturer",
            flex: 1,
            minWidth: 100,
            editable: false,
            sortable: true,
        },
        {
            field: "model",
            headerName: "Model",
            flex: 1,
            minWidth: 100,
            editable: false,
            sortable: true,
        },
        {
            field: 'action',
            headerName: '',
            type: 'actions',
            width: 150,
            sortable: false,
            filterable: false,
            disableClickEventBubbling: true,

            renderCell: (params) => {
                return (
                    <Stack direction="row" spacing={1}>
                        <Button variant="contained" color="warning" size="small" onClick={() => editCustomerItemRowClick(params.row)}>Edit</Button>
                        <Button variant="contained" color="primary" size="small" onClick={() => historyCustomerItemRowClick(params.row)}>History</Button>
                    </Stack>
                );
            },
        }
    ]

    React.useEffect(() => {
        if (open && rowData.rows === undefined) {
            getCustomerItems()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    return (
        <Box>
            <DataGrid
                key={open}
                rows={rowData.rows}
                columns={columns}
                autoHeight={true}
                loading={isLoading}
                pageSizeOptions={[5, 10, 25]}
                pagination
                disableRowSelectionOnClick
            />
            <CustomerItemModal
                {...customerItemModal}
                closeModal={closeCustomerItemModal}
            />
            <CustomerItemHistoryModal
                {...customerItemHistoryModal}
                closeModal={(selected) => {
                    setCustomerItemHistoryModal({ ...customerItemHistoryModal, open: false })
                    if (selected) {
                        closeModal(false)
                    }
                }}
            />
        </Box>
    )

}

const CustomerServiceOrders = ({ customer, open, closeModal }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [my_user,] = useTrackedUser();
    const [rowData, setRowData] = React.useState({ rows: undefined, total_count: 0 })
    const [isLoading, setIsLoading] = React.useState(true)
    const navigate = useNavigate();
    React.useEffect(() => {
        if (open && rowData.rows === undefined) {
            getServiceOrders()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    const getServiceOrders = () => {
        setIsLoading(true)
        const filters = { 'customer_id.eq': customer.id }
        send_authorized_request(() =>
            AXIOS_API_INSTANCE.get("serviceorders", {
                params: {
                    order_by: 'date_created',
                    order_by_direction: 'desc',
                    filters: JSON.stringify(filters)
                },
            }).then((serviceorders) => {
                console.log(serviceorders)
                setRowData({
                    rows: serviceorders.data.data,
                    total_count: serviceorders.data.total_count,
                })
                setIsLoading(false)
            }
            ).catch((error) => {
                enqueueSnackbar(`Failed to load service orders: ${error.response?.data?.message ?? "Unknown Error Occurred"}`, { variant: 'error' })
                setIsLoading(false)
            })
        )
    }

    const serviceOrdersRowClick = (data, event) => {
        if (event.ctrlKey) {
            window.open(`/ServiceOrders/${data.row.id}#details`)
        } else {
            closeModal(true)
            navigate(`/ServiceOrders/${data.row.id}#details`)
        }
    }

    const columns = [
        {
            field: "status_name",
            headerName: "Status",
            width: 200,
            editable: false,
            sortable: true,
            renderCell: (params) => {
                return serviceOrderChip(my_user, params.row.status_name, params.row.status_type)
            }
        },
        {
            field: "date_created",
            headerName: "Created",
            type: 'datetime',
            editable: false,
            sortable: true,
            flex: 1,
            renderCell: (params) => {
                return (
                    <span>{dayjs.utc(params.row.date_created).local().format("MM/DD/YYYY")}</span>
                )
            }
        },
        {
            field: "manufacturer",
            headerName: "Manufacturer",
            minWidth: 120,
            flex: 1,
            editable: false,
            sortable: true,
            filterable: false,
            valueGetter: (value, row) => row.customer_item.manufacturer || ""
        },
        {
            field: "model",
            headerName: "Model",
            minWidth: 120,
            flex: 1,
            editable: false,
            sortable: true,
            filterable: false,
            valueGetter: (value, row) => row.customer_item.model || ""
        },
        {
            field: "identifier",
            headerName: "Identifier",
            minWidth: 120,
            flex: 1,
            editable: false,
            sortable: true,
            filterable: false,
            valueGetter: (value, row) => row.customer_item.identifier || ""
        },
    ]

    return (
        <DataGrid
            key={open}
            rows={rowData.rows}
            columns={columns}
            autoHeight={true}
            loading={isLoading}
            pageSizeOptions={[5, 10, 25]}
            pagination
            onRowClick={serviceOrdersRowClick}
            disableRowSelectionOnClick
        />
    )

}

const CustomerChat = ({ customer, open, closeModal }) => {
    const { enqueueSnackbar } = useSnackbar();
    const { refetch: refetchTotalUnread } = useSmsUnread();
    const [isLoading, setIsLoading] = React.useState(true)

    const [contacts, setContacts] = React.useState([]);
    const [messages, setMessages] = React.useState([]);

    const [selectedContact, setSelectedContact] = React.useState(null);

    const sortedContacts = contacts.sort((a, b) => b.lastMessageTimestamp - a.lastMessageTimestamp);



    const fetchContacts = () => {
        setIsLoading(true)

        send_authorized_request(() => {
            refetchTotalUnread()
            AXIOS_API_INSTANCE.get("customercontacts", {
                params: {
                    order_by: 'first_name',
                    order_by_direction: 'asc',
                    filters: JSON.stringify({ 'customer_id.eq': customer.id })
                },
            }).then((customercontacts) => {
                const filtered_contacts = customercontacts.data.data.filter(contact => contact.phone && contact.phone.trim())
                const mapped_contacts = filtered_contacts.map((c) => {
                    return {
                        customers: [{ name: c.first_name + " " + c.last_name, id: c.id }],
                        date_created: "",
                        phone: c.phone,
                        unread: 0,
                    }
                })
                if (customer.phone && customer.phone.trim()) {
                    const existing_contact = mapped_contacts.find(c => c.phone === customer.phone)
                    if (existing_contact) {
                        existing_contact.customers.push({ name: customer.name, id: customer.id })
                    } else {
                        mapped_contacts.push({
                            customers: [{ name: customer.name, id: -1 }],
                            date_created: "",
                            phone: customer.phone,
                            unread: 0,
                        })
                    }
                }
                setContacts(mapped_contacts)
                setIsLoading(false)
            }
            ).catch((error) => {
                enqueueSnackbar(`Failed to load customer contacts: ${error.response?.data?.message ?? "Unknown Error Occurred"}`, { variant: 'error' })
                setIsLoading(false)
            })
        })
    }

    const fetchMessages = () => {
        setIsLoading(true)

        send_authorized_request(() =>
            AXIOS_API_INSTANCE.get(`smsmessages/${selectedContact.phone}`, {

            }).then((messages) => {
                setMessages(messages.data.data)

                setIsLoading(false)
            }).catch((error) => {
                setIsLoading(false)
                enqueueSnackbar(`Failed to get messages <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
            })
        )
    }

    React.useEffect(() => {
        if (selectedContact) {
            fetchMessages();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedContact]);

    React.useEffect(() => {
        if (open && contacts.length === 0) {
            fetchContacts()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    return (
        <ChatAppStyledPaper>
            <AppBar position="static">
                <Toolbar>
                    <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                        Messages
                    </Typography>
                </Toolbar>
            </AppBar>

            <Grid container spacing={2}>
                <Grid size={4} sx={{ height: "50vh", overflow: "auto" }}>
                    <ChatAppContacts
                        contacts={sortedContacts}
                        selectedContact={selectedContact}
                        setSelectedContact={(contact) => {
                            setMessages([])
                            setSelectedContact(contact)
                        }}
                    />
                </Grid>
                <Grid size={8}>

                    {selectedContact &&
                        <Box ref={messages} sx={{ height: "50vh", overflow: 'auto' }}>
                            {isLoading &&
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        height: '100%', // Adjust the height as needed
                                    }}
                                >
                                    <CircularProgress size="7rem" />
                                </Box>}
                            <ChatAppMessages
                                messages={messages}
                            />
                        </Box>
                    }
                    {selectedContact &&
                        <ChatAppTypeMessage
                            selectedContact={selectedContact}
                            setMessages={setMessages}
                            setIsLoading={setIsLoading}
                            refetch={() => {
                                fetchContacts();
                                fetchMessages();
                            }}
                        />
                    }


                </Grid>
            </Grid>
        </ChatAppStyledPaper>
    )
}



export default function CustomerModal({
    customer,
    open,
    closeModal,
    creatingNewCustomer,
    showSMS = true
}) {
    const [tab, setTab] = React.useState("details")

    React.useEffect(() => {
        if (!open) {
            setTab('details')
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customer, open])

    return (
        (<div>
            <Dialog
                open={open}
                onClose={() => closeModal(false)}
                maxWidth={'lg'}
                fullWidth
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <DialogTitle>
                    {creatingNewCustomer ? "Creating Customer" : "Customer"}
                    <IconButton
                        aria-label="close"
                        onClick={() => closeModal(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                {!creatingNewCustomer && <DialogContent
                    sx={{ minHeight: '400px' }}>
                    <Tabs
                        value={tab}
                        onChange={(e, value) => setTab(value)}
                        variant="scrollable"
                    >
                        <Tab label="Details" value="details" sx={{ fontWeight: "bold" }} />
                        <Tab label="Contacts" value="contacts" sx={{ fontWeight: "bold" }} />
                        <Tab label="Items" value="customeritems" sx={{ fontWeight: "bold" }} />
                        <Tab label="Service Orders" value="serviceorders" sx={{ fontWeight: "bold" }} />
                        {showSMS && <Tab label="Chat" value="chat" sx={{ fontWeight: "bold" }} />}
                    </Tabs >
                    <TabPanel value={tab} value_key={"details"} sx={{ p: 0, pt: 1 }}>
                        <CustomerDetails
                            customer={customer}
                            closeModal={closeModal}
                            creatingNewCustomer={creatingNewCustomer}
                        />
                    </TabPanel>
                    <TabPanel value={tab} value_key={"contacts"} sx={{ p: 0, pt: 1 }} preserve>
                        <CustomerContacts
                            customer={customer}
                            open={tab === 'contacts'}
                            closeModal={closeModal}
                        />
                    </TabPanel>
                    <TabPanel value={tab} value_key={"customeritems"} sx={{ p: 0, pt: 1 }} preserve>
                        <CustomerCustomerItems
                            customer={customer}
                            open={tab === 'customeritems'}
                            closeModal={(e) => { closeModal(e) }}
                        />
                    </TabPanel>
                    <TabPanel value={tab} value_key={"serviceorders"} sx={{ p: 0, pt: 1 }} preserve>
                        <CustomerServiceOrders
                            customer={customer}
                            open={tab === 'serviceorders'}
                            closeModal={(e) => { closeModal(e) }}
                        />
                    </TabPanel>
                    <TabPanel value={tab} value_key={"chat"} sx={{ p: 0, pt: 1 }} preserve>
                        <CustomerChat
                            customer={customer}
                            open={tab === 'chat'}
                            closeModal={(e) => { closeModal(e) }}
                        />
                    </TabPanel>
                </DialogContent>}
                {creatingNewCustomer && <DialogContent>
                    <CustomerDetails
                        customer={customer}
                        closeModal={closeModal}
                        creatingNewCustomer={creatingNewCustomer}
                    />
                </DialogContent>}
            </Dialog>
        </div>)
    );
}
